import type {
  DeliveryNoteAssignmentProposal,
  ProposeDeliveryNoteAssignmentVariables,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import {
  buildResponseValidationReport,
  type KyRequestOptions,
} from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import {
  zodDeliveryNoteAssignmentProposalPayloadSchema,
  zodDeliveryNoteAssignmentProposalSchema,
} from './validations';

/**
 * Asks which invoice line each article of the selected delivery notes belongs to.
 *
 * @param variables - The invoice id and the selected delivery note ids.
 * @param options - Optional request options, such as an abort signal.
 * @returns The proposal: one row per article, the candidate positions and whether a second step is needed.
 * @throws Error if the response does not match the expected shape.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices
 */
export async function proposeDeliveryNoteAssignment(
  { invoiceId, payload }: ProposeDeliveryNoteAssignmentVariables,
  options?: KyRequestOptions,
): Promise<DeliveryNoteAssignmentProposal> {
  const response = await vestigasApi
    .post(ENDPOINT.INVOICE.PROPOSE_DELIVERY_NOTE_ASSIGNMENT(invoiceId), {
      json: payload,
      ...options,
      validate: {
        ...options?.validate,
        body: zodDeliveryNoteAssignmentProposalPayloadSchema,
      },
    })
    .json();

  const parsed = zodDeliveryNoteAssignmentProposalSchema.safeParse(response);

  if (!parsed.success) {
    log.responseValidation({
      ...buildResponseValidationReport(parsed.error.issues),
      key: 'propose-delivery-note-assignment',
      message:
        'delivery note assignment proposal response has validation errors',
    });

    throw new Error(
      'Delivery note assignment proposal response did not match the expected shape',
    );
  }

  return parsed.data;
}
